import React from 'react';
import { AlertTriangle, X, ArchiveRestore, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRecoveryLogic } from './hooks/useRecoveryLogic';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

const RecoveryFileCardContent: React.FC = () => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = React.useState(false);
  const {
    firstRecord,
    isRecovering,
    progress,
    hasMultiple,
    recentRecoverableFiles,
    handleRecover,
    handleDismiss,
    getTimeAgo,
  } = useRecoveryLogic();

  const handleRecoverClick = () => {
    if (firstRecord) {
      handleRecover(firstRecord);
    }
  };

  const handleDismissClick = (fileId: string) => {
    handleDismiss(fileId);
  };

  return (
    <>
      <style>{`
        @keyframes expandIn {
          0% {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
      <Card
        className="relative flex flex-col h-full shadow-none bg-transparent border-0 transition-all duration-200 ease-out transform scale-100 cursor-pointer hover:scale-102 animate-[expandIn_0.3s_ease-out]"
        onClick={handleRecoverClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col h-full">
          {/* Thumbnail Area with Recovery Icon */}
          <div className="relative">
            <div className="w-full aspect-video rounded-lg bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center relative">
              <div className="flex flex-col items-center gap-1">
                <div className="relative w-8 h-8">
                  <ArchiveRestore
                    className={`absolute w-8 h-8 text-gray-600 dark:text-gray-400 transition-all duration-500 ease-in-out transform ${
                      isHovered ? 'opacity-70 scale-110' : 'opacity-0 scale-95'
                    }`}
                  />
                  <AlertTriangle
                    className={`absolute w-8 h-8 text-gray-600 dark:text-gray-400 transition-all duration-500 ease-in-out transform ${
                      isHovered ? 'opacity-0 scale-95' : 'opacity-70 scale-100'
                    }`}
                  />
                </div>
                <div className="relative h-4 flex items-center justify-center w-full">
                  <span
                    className={`absolute text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap transition-all duration-500 ease-in-out ${
                      isHovered ? 'opacity-80' : 'opacity-0'
                    }`}
                  >
                    {t('library.recovery.status.finalize')}
                  </span>
                  <span
                    className={`absolute text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap transition-all duration-500 ease-in-out ${
                      isHovered ? 'opacity-0' : 'opacity-80'
                    }`}
                  >
                    {t('library.recovery.status.interrupted')}
                  </span>
                </div>
              </div>

              {/* Dismiss Button */}
              <div className="absolute top-2 right-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-8 h-8 p-0 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 border-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (firstRecord) {
                            handleDismissClick(firstRecord.fileId);
                          }
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('library.recovery.dismiss')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {/* Progress indicator in bottom right */}
              {isRecovering && (
                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white rounded-xl text-xs font-medium flex items-center gap-2">
                  <Loader className="w-3.5 h-3.5 animate-spin text-white" />
                  {Math.round(progress)}%
                </div>
              )}
            </div>
          </div>

          {/* Content Area - Matching FileCard structure */}
          <CardContent className="flex-grow flex flex-col justify-start py-4 px-0 bg-transparent">
            <div className="flex items-start justify-between relative">
              <div className="flex items-start w-full overflow-hidden">
                {/* Recovery Avatar - Matching FileCard exactly */}
                <Avatar className="w-8 h-8 bg-orange-500 text-white text-lg font-bold">
                  <AvatarFallback className="bg-orange-500 text-white text-lg font-bold">
                    !
                  </AvatarFallback>
                </Avatar>

                <div className="ml-2 flex-grow overflow-hidden w-full min-h-[4.5rem]">
                  <h3 className="font-semibold w-full overflow-hidden line-clamp-2 leading-6 pr-10 break-words">
                    {hasMultiple
                      ? t('library.recovery.title.multiple', {
                          count: recentRecoverableFiles.length,
                        })
                      : t('library.recovery.title.single')}
                  </h3>
                  <div className="pl-px">
                    <p className="text-xs text-gray-600 dark:text-gray-400 opacity-80 mt-1 flex items-center">
                      {firstRecord && getTimeAgo(firstRecord.startedAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </div>
      </Card>
    </>
  );
};

// Main component that includes Grid wrapper
const RecoveryFileCard: React.FC = () => {
  const { hasRecoverableFiles } = useRecoveryLogic();

  // Don't render if no recent recoverable files
  if (!hasRecoverableFiles) {
    return null;
  }

  return (
    <div className="col-span-1">
      <RecoveryFileCardContent />
    </div>
  );
};

export default RecoveryFileCard;
